// ── Model options for the chat mode picker ──────────────────────────────────

export type ModelCategory =
  | "General Purpose"
  | "Coding"
  | "Creative Writing"
  | "Open Source";

export interface ModelOption {
  value: string;
  label: string;
  desc: string;
  /** Whether this model supports image input (vision). */
  vision?: boolean;
  /** Whether this model can generate image outputs. */
  imageGeneration?: boolean;
  /** Whether this model can generate video outputs. */
  videoGeneration?: boolean;
  /** Category for grouped display in the picker. */
  category: ModelCategory;
}

/** Display order and emoji for each model category */
export const modelCategoryMeta: Record<
  ModelCategory,
  { emoji: string; order: number }
> = {
  "General Purpose": { emoji: "⚡", order: 0 },
  Coding: { emoji: "💻", order: 1 },
  "Creative Writing": { emoji: "✍️", order: 2 },
  "Open Source": { emoji: "🔓", order: 3 },
};

/** Return categories in display order */
export function getOrderedModelCategories(): ModelCategory[] {
  return (Object.keys(modelCategoryMeta) as ModelCategory[]).sort(
    (a, b) => modelCategoryMeta[a].order - modelCategoryMeta[b].order,
  );
}

/** Group model options by category */
export function getModelsByCategory(
  models: ModelOption[],
): Record<ModelCategory, ModelOption[]> {
  const result = {} as Record<ModelCategory, ModelOption[]>;
  for (const cat of getOrderedModelCategories()) {
    result[cat] = models.filter((m) => m.category === cat);
  }
  return result;
}

/** Free tier — all :free models, no cost to the user */
export const freeModelOptions: ModelOption[] = [
  {
    value: "openai/gpt-oss-120b:free",
    label: "GPT-OSS 120B",
    desc: "Best free reasoning model — great for most things.",
    category: "General Purpose",
  },
  {
    value: "meta-llama/llama-3.3-70b-instruct:free",
    label: "Llama 3.3 70B",
    desc: "Fast open-source model, solid at following instructions.",
    category: "Open Source",
  },
  {
    value: "google/gemma-3-27b-it:free",
    label: "Gemma 3 27B",
    desc: "Google's lightweight open model, good for quick answers.",
    vision: true,
    category: "Open Source",
  },
  {
    value: "mistralai/mistral-small-3.1-24b-instruct:free",
    label: "Mistral Small 3.1",
    desc: "Snappy European model, good at writing and analysis.",
    vision: true,
    category: "General Purpose",
  },
  {
    value: "qwen/qwen3-coder:free",
    label: "Qwen3 Coder (free)",
    desc: "Best free coding model.",
    category: "Coding",
  },
  {
    value: "minimax/minimax-m2.5:free",
    label: "MiniMax M2.5",
    desc: "Fun for creative writing and roleplay.",
    category: "Creative Writing",
  },
];
/** Paid tier — affordable paid models + free models (requires chat credits for use) */
export const paidModelOptions: ModelOption[] = [
  // ── General Purpose ──────────────────────────────────────────────────────
  {
    value: "openai/gpt-5-nano",
    label: "GPT-5 Nano",
    desc: "Fast and capable",
    vision: true,
    category: "General Purpose",
  },
  {
    value: "ai21/jamba-large-1.7",
    label: "Jamba Large 1.7",
    desc: "1:7 transformer:SSM architecture",
    vision: false,
    category: "General Purpose",
  },
  {
    value: "google/gemini-3.1-flash-preview",
    label: "Gemini 3.1 Flash",
    desc: "Great at reasoning and long context.",
    vision: true,
    category: "General Purpose",
  },
  {
    value: "anthropic/claude-haiku-4.5",
    label: "Claude Haiku 4.5",
    desc: "Brilliant at nuanced writing.",
    vision: true,
    category: "General Purpose",
  },
  {
    value: "mistralai/mistral-large-2512",
    label: "Mistral Large 2512",
    desc: "Great at multilingual tasks.",
    vision: true,
    category: "General Purpose",
  },
  {
    value: "x-ai/grok-4.1-fast",
    label: "Grok 4.1 Fast",
    desc: "Witty — great for fun conversations.",
    vision: true,
    category: "General Purpose",
  },
  {
    value: "xiaomi/mimo-v2-pro",
    label: "MiMo v2 Pro",
    desc: "Xiaomi's top multimodal model — vision + reasoning.",
    vision: true,
    category: "General Purpose",
  },
  {
    value: "xiaomi/mimo-v2-omni",
    label: "MiMo v2 Omni",
    desc: "Xiaomi's omni model — text, vision, and more.",
    vision: true,
    category: "General Purpose",
  },
  // ── Coding ───────────────────────────────────────────────────────────────
  {
    value: "qwen/qwen3-coder-next",
    label: "Qwen3 Coder next",
    desc: "Top-tier coding assistant.",
    category: "Coding",
  },
  // ── Creative Writing ─────────────────────────────────────────────────────
  {
    value: "minimax/minimax-m2.7",
    label: "MiniMax M2.7",
    desc: "Excellent for creative fiction, roleplay, and storytelling.",
    category: "Creative Writing",
  },
  {
    value: "thedrummer/rocinante-12b",
    label: "Rocinante 12B",
    desc: "Compact storytelling — vivid prose and immersive narratives.",
    category: "Creative Writing",
  },
  {
    value: "thedrummer/cydonia-24b-v4.1",
    label: "Cydonia 24B v4.1",
    desc: "Rich writing — excels at long-form fiction and dialogue.",
    category: "Creative Writing",
  },
  {
    value: "thedrummer/skyfall-36b-v2",
    label: "Skyfall 36B v2",
    desc: "Storytelling powerhouse — nuanced characters and world-building.",
    category: "Creative Writing",
  },
  // ── Free models still available to all logged-in users ────────────────────
  ...freeModelOptions,
];
